import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronDown } from 'lucide-react';
import { ValidationIssue } from '@/types/payroll-validator';
import { IssueDetailCard } from './IssueDetailCard';

interface CategoryIssueGroupProps {
  category: string;
  categoryInfo: {
    title: string;
    icon: React.ReactNode;
    color: string;
  };
  issues: ValidationIssue[];
}

export const CategoryIssueGroup: React.FC<CategoryIssueGroupProps> = ({
  category,
  categoryInfo,
  issues,
}) => {
  const [expanded, setExpanded] = useState(true);

  const criticalCount = issues.filter((i) => i.severity === 'critical').length;
  const warningCount = issues.filter((i) => i.severity === 'warning').length;
  const infoCount = issues.filter((i) => i.severity === 'info').length;

  return (
    <Card className={`border-l-4 ${categoryInfo.color}`}>
      <CardHeader
        className="cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${categoryInfo.color}`}>
              {categoryInfo.icon}
            </div>
            <div>
              <CardTitle className="text-xl">{categoryInfo.title}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-600">
                  {issues.length} issue{issues.length !== 1 ? 's' : ''} found
                </span>
                {criticalCount > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {criticalCount} critical
                  </Badge>
                )}
                {warningCount > 0 && (
                  <Badge className="bg-orange-100 text-orange-800 text-xs">
                    {warningCount} warning{warningCount !== 1 ? 's' : ''}
                  </Badge>
                )}
                {infoCount > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {infoCount} info
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
              expanded ? 'rotate-180' : ''
            }`}
          />
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 pb-4">
          <div className="space-y-2">
            {issues
              .sort((a, b) => {
                // Sort by severity: critical > warning > info
                const severityOrder = { critical: 0, warning: 1, info: 2 };
                return severityOrder[a.severity] - severityOrder[b.severity];
              })
              .map((issue) => (
                <IssueDetailCard key={issue.issue_id} issue={issue} />
              ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

